/**
 * 主线程封装：与 public/wasm/codec2/codec2EncodeWorker.js 通信（与 c2enc 同路径，避免 Nginx 对 /workers 返回 404）。
 */

import { getCodec2Mode } from '../../constants/Codec2Constants';

const DEFAULT_TIMEOUT_MS = 15000;

function workerScriptUrl(): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const path = `${base}/wasm/codec2/codec2EncodeWorker.js`;
  if (typeof window === 'undefined') {
    return path;
  }
  return `${window.location.origin}${path}`;
}

export type Codec2EncodeResult =
  | { ok: true; bit: ArrayBuffer }
  | { ok: false; error: string };

export class Codec2EncodeClient {
  private worker: Worker | null = null;
  private nextId = 1;
  private pending = new Map<
    number,
    { resolve: (v: Codec2EncodeResult) => void; reject: (e: Error) => void; timer: number }
  >();

  private ensureWorker(): Worker {
    if (this.worker) {
      return this.worker;
    }
    const url = workerScriptUrl();
    this.worker = new Worker(url);
    this.worker.onmessage = (ev: MessageEvent) => {
      const data = ev.data;
      if (!data || data.type !== 'encoded') {
        return;
      }
      const id = data.id as number;
      const slot = this.pending.get(id);
      if (!slot) {
        return;
      }
      window.clearTimeout(slot.timer);
      this.pending.delete(id);
      if (data.ok) {
        const bit = data.payload as ArrayBuffer | undefined;
        if (!bit || bit.byteLength === 0) {
          slot.resolve({ ok: false, error: 'Codec2 encode returned empty bit payload' });
        } else {
          slot.resolve({ ok: true, bit });
        }
      } else {
        slot.resolve({ ok: false, error: String(data.error || 'encode failed') });
      }
    };
    this.worker.onerror = (e) => {
      const errMsg = e.message || 'Codec2 worker error';
      this.pending.forEach((slot) => {
        window.clearTimeout(slot.timer);
        slot.resolve({ ok: false, error: errMsg });
      });
      this.pending.clear();
    };
    return this.worker;
  }

  /**
   * @param mode Codec2 模式字符串，须与设备/后端一致（默认来自 REACT_APP_CODEC2_MODE 或 3200）
   * @param pcm 8 kHz、单声道、16 bit 小端裸 PCM
   */
  encode(mode: string | undefined, pcm: ArrayBuffer): Promise<Codec2EncodeResult> {
    const worker = this.ensureWorker();
    const resolvedMode = mode && mode.length > 0 ? mode : getCodec2Mode();
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        resolve({ ok: false, error: `Codec2 encode timeout (${DEFAULT_TIMEOUT_MS}ms)` });
      }, DEFAULT_TIMEOUT_MS);
      this.pending.set(id, { resolve, reject, timer });
      try {
        worker.postMessage({ type: 'encode', id, mode: resolvedMode, pcm }, [pcm]);
      } catch (e) {
        window.clearTimeout(timer);
        this.pending.delete(id);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pending.forEach((slot) => {
      window.clearTimeout(slot.timer);
      slot.resolve({ ok: false, error: 'worker terminated' });
    });
    this.pending.clear();
  }
}

/** 进程内单例，避免重复起多个 Worker */
let singleton: Codec2EncodeClient | null = null;

export function getCodec2EncodeClient(): Codec2EncodeClient {
  if (!singleton) {
    singleton = new Codec2EncodeClient();
  }
  return singleton;
}

export function disposeCodec2EncodeClient(): void {
  singleton?.terminate();
  singleton = null;
}
